import { type ReactNode } from 'react';
import { type PairingMutation } from '@hungryroot/cart';
import { type PairingV1 as Pairing } from '@hungryroot/rest';
import {
  Ingredient,
  PointsPlacementVariant,
  type IngredientViewModel,
} from '../../../../Ingredient/Ingredient';
import { type UseEditRecipeResult } from '../../../hooks/useEditRecipe/useEditRecipe';
import { EditButtons } from './EditButtons/EditButtons';

interface Props {
  testid?: string;
  onBackButtonClick: () => void;
  onIngredientAdd: (ingredientId: number) => void;
  onIngredientRemoveClick?: (ingredient: IngredientViewModel) => void;
  editRecipeQuery: UseEditRecipeResult;
  ingredients: IngredientViewModel[];
  modified: {
    ingredients: number[];
  };
  onSwapClick: (params: {
    ingredient: IngredientViewModel;
    swappablePairings: Pairing[];
  }) => void;
  swappablePairings?: Record<string, Pairing[]>;
  isModalEditor?: boolean;
  editorIcons: { swap: ReactNode; remove: ReactNode; add: ReactNode };
  onUpdate?: (modifications: PairingMutation[]) => void;
}

export function IngredientEdit(props: Props) {
  const {
    onIngredientAdd,
    editRecipeQuery,
    modified,
    onSwapClick,
    testid,
    onIngredientRemoveClick,
    ingredients,
    swappablePairings,
    isModalEditor = false,
    editorIcons,
    onUpdate,
  } = props;
  const { swap, addIngredient } = editRecipeQuery;

  return (
    <div data-testid={testid}>
      {ingredients.map((ingredient) => {
        const { id: ingredientId } = ingredient;
        const key = `ingredient-${ingredientId}`;
        const removed = modified.ingredients.includes(ingredientId) === false;
        const swappable = swappablePairings && swappablePairings[ingredientId];

        return (
          <Ingredient
            testid={`${testid}-${key}`}
            ingredient={ingredient}
            key={key}
            isModalEditor={isModalEditor}
            pointsPlacementVariant={
              isModalEditor ? PointsPlacementVariant.Edit : null
            }
            actions={
              <EditButtons
                testid={`${testid}-${key}`}
                removed={removed}
                ingredient={ingredient}
                onSwapClick={
                  swap === undefined || swappable === undefined
                    ? undefined
                    : () => {
                        onSwapClick({
                          swappablePairings: swappable,
                          ingredient,
                        });
                      }
                }
                onAddClick={
                  addIngredient === undefined
                    ? undefined
                    : () => {
                        isModalEditor
                          ? addIngredient(ingredientId, onUpdate)
                          : addIngredient(ingredientId);

                        onIngredientAdd(ingredientId);
                      }
                }
                onRemoveClick={
                  onIngredientRemoveClick === undefined
                    ? undefined
                    : () => {
                        onIngredientRemoveClick(ingredient);
                      }
                }
                editorIcons={editorIcons}
              />
            }
          />
        );
      })}
    </div>
  );
}
